import React from "react";
import { Link, useHistory } from "react-router-dom";

import {
  Card,
  CardContent,
  Grid,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { orange } from "@material-ui/core/colors";

import { Button, SelectField, TextField } from "react-admin";

import {
  ExceptionIcon,
  ReturnIcon,
  TrainIcon,
} from "../../../components/common/iconography";
import { PhotoViewer } from "../../../components/common/PhotoViewer";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";

import { isAuthorized } from "../../../utils/appUtils";
import {
  AclType,
  CarrierType,
  InboundStates,
  InboundStatus,
  InboundStatuses,
} from "../../../utils/constants";
import { getTrackCarLocationDisplayLabel } from "../../../utils/locationUtils";
import { deepRedirect, formatCurrency, getFormTab } from "../../../utils/utils";

import { getTotalInvoiced } from "../invoices/InvoiceUtils";
import { InboundStateIcon } from "./InboundStateIcon";

const useAsideStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      width: "18em",
      minWidth: "18em",
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.5),
      overflow: "initial",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  status: {
    marginTop: theme.spacing(0.3),
  },
  exceptionButton: {
    textAlign: "center",
  },
}));

export const InboundAside = ({
  backPath,
  record,
  exceptions,
  photos,
  ...props
}) => {
  const classes = useAsideStyles();
  const history = useHistory();
  var tab = getFormTab(props);
  const linkBackPath = backPath
    ? [...backPath, props.location.pathname]
    : [props.location.pathname];
  const invoiceLinkId = record?.invoice?.id || (record?.invoiceId || "").trim();

  const handleTrackClick = () => {
    deepRedirect(history, "/trackManagementAdmin", backPath, {
      railCarNumber: record.railCarNumber,
    });
  };

  if (!record || tab === "1" || tab === "tallySheet") return null;
  return (
    <Card className={classes.root}>
      <CardContent>
        <Grid container direction="column" spacing={1} fullWidth>
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Status
            </Typography>
            <Grid container direction="row">
              <Grid item xs={2}>
                <InboundStateIcon record={record} />
              </Grid>
              <Grid item xs={10} className={classes.status}>
                <SelectField
                  source="status"
                  choices={InboundStatuses}
                  record={record}
                />
                ,&nbsp;
                <SelectField
                  source="state"
                  choices={InboundStates}
                  record={record}
                />
              </Grid>
            </Grid>
            {record.returnShipment && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={record?.returnReason ?? "Return Shipment"}
                    placement="left"
                    arrow
                  >
                    <ReturnIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">Return Shipment</Typography>
                </Grid>
              </Grid>
            )}
            {exceptions.hasException && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Exceptions
                </Typography>
              </Grid>
            )}
            {exceptions.hasException && exceptions.unsafeShipment && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={
                      exceptions.unsafeShipmentNote
                        ? exceptions.unsafeShipmentNote
                        : "Unsafe Shipment"
                    }
                    placement="left"
                    arrow
                  >
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">Unsafe Shipment</Typography>
                </Grid>
              </Grid>
            )}
            {exceptions.hasException && exceptions.brokenSeal && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={
                      exceptions.brokenSealNote
                        ? exceptions.brokenSealNote
                        : "Broken Seal"
                    }
                    placement="left"
                    arrow
                  >
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">Broken Seal</Typography>
                </Grid>
              </Grid>
            )}
            {exceptions.hasException && exceptions.otherException && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={
                      exceptions.otherExceptionNote
                        ? exceptions.otherExceptionNote
                        : "Other Exception"
                    }
                    placement="left"
                    arrow
                  >
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">Other Exception</Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
          {record.trackCar && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Location:
              </Typography>
              <br />
              <Typography variant="body2">
                {getTrackCarLocationDisplayLabel(record.trackCar) ??
                  "NOT SPOTTED"}
              </Typography>
              {record.carrierType === CarrierType.RAIL && record.trackCar && (
                <Button label="MAP" color="default" onClick={handleTrackClick}>
                  <TrainIcon fontSize="small" />
                </Button>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Account:
            </Typography>
            <br />
            {isAuthorized(AclType.ACCOUNTS) ? (
              <Link
                to={{
                  pathname: `/accounts/${record.accountId}`,
                  state: { backPath: linkBackPath },
                }}
              >
                <Typography variant="body2" color="primary">
                  {accountNameRenderer(record.account)}
                </Typography>
              </Link>
            ) : (
              <Link
                to={{
                  pathname: `/accounts/${record.accountId}/show`,
                  state: { backPath: linkBackPath },
                }}
              >
                <Typography variant="body2" color="primary">
                  {accountNameRenderer(record.account)}
                </Typography>
              </Link>
            )}
          </Grid>
          {record.billToAccount &&
            record.billToAccountId !== record.accountId && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Billing Account:
                </Typography>
                <br />
                <Link
                  to={{
                    pathname: `/accounts/${record.billToAccountId}${isAuthorized(AclType.ACCOUNTS) ? "" : "/show"}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {accountNameRenderer(record.billToAccount)}
                  </Typography>
                </Link>
              </Grid>
            )}
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              PO Number
            </Typography>
            <br />
            <TextField source="poNumber" record={record} />
          </Grid>
          {record.railCarNumber && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                {record.carrierType === CarrierType.RAIL
                  ? "Railcar Number"
                  : "Trailer Number"}
              </Typography>
              <br />
              <TextField source="railCarNumber" record={record} />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Lot Number
            </Typography>
            <br />
            <TextField source="lotNumber" record={record} />
          </Grid>
          {record.invoice &&
            invoiceLinkId &&
            isAuthorized(AclType.INVOICES) && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Invoice
                </Typography>
                <br />
                <Link
                  to={{
                    pathname: `/invoices/${invoiceLinkId}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {record.invoice.invoiceNumber} :{" "}
                    {formatCurrency(getTotalInvoiced(record))}
                  </Typography>
                </Link>
              </Grid>
            )}
          {/* Only relevant while the advice is under review — once it is
              approved (IN_TRANSIT and beyond) the submitter is noise. */}
          {record.advisedBy &&
            record.status?.startsWith(InboundStatus.ADVICE_ALL) && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Advised By
                </Typography>
                <br />
                <TextField source="advisedBy" record={record} />
              </Grid>
            )}
          {record.unloadedBy && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Unloaded By
              </Typography>
              <br />
              <TextField source="unloadedBy" record={record} />
            </Grid>
          )}
          {record.reviewedBy && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Reviewed By
              </Typography>
              <br />
              <TextField source="reviewedBy" record={record} />
            </Grid>
          )}
          <PhotoViewer photos={photos} />
        </Grid>
      </CardContent>
    </Card>
  );
};
